import type {Question} from 'src/types/survey';
import type {EntityType} from 'src/types/ats-entities';
import type {
  WritebackAllowedAttributesKeyed,
  AttributeMetaData,
} from 'src/types/writeback';

import * as React from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'src/rerouter';

import {selectWorkflow} from 'src/selectors/workflow';
import {useWritebackAllowedAttributesKeyed} from 'src/hooks/useWritebackAllowedAttributes';


export type QuestionType =
  | 'text_survey_question'
  | 'rating_scale_survey_question'
  | 'nps_survey_question'
  | 'multiple_choice_survey_question'
  | 'file_upload_survey_question'
  | 'calendar_date_survey_question';

type CurrentQuestion = {
  id?: string;
  selectedType: QuestionType;
  originalType: QuestionType;
  // @ts-expect-error - TS1337 - An index signature parameter type cannot be a literal type or generic type. Consider using a mapped object type instead.
  [key: QuestionType]: Question;
};

export type WritebackContextState = {
  question: CurrentQuestion;
  module_attributes: Partial<Record<QuestionType, string[]>> | null | undefined;
  entity_type: EntityType | null | undefined;
  attribute_meta_data:
    | {
        [key: string]: AttributeMetaData;
      }
    | null
    | undefined;
};

type ReducerTypes =
  | 'RECEIVE_ALLOWED_ATTRIBUTES'
  | 'RECEIVE_QUESTION'
  | 'UPDATE_QUESTION'
  | 'UPDATE_QUESTION_TYPE'
  | 'RESET_STATE';

export const RECEIVE_ALLOWED_ATTRIBUTES = 'RECEIVE_ALLOWED_ATTRIBUTES';
export const RECEIVE_QUESTION = 'RECEIVE_QUESTION';
export const UPDATE_QUESTION = 'UPDATE_QUESTION';
export const UPDATE_QUESTION_TYPE = 'UPDATE_QUESTION_TYPE';
export const RESET_STATE = 'RESET_STATE';

type ReceiveAllowedAttributesAction = {
  type: 'RECEIVE_ALLOWED_ATTRIBUTES';
  payload: WritebackAllowedAttributesKeyed | null | undefined;
};

type UpdateQuestionAction = {
  type: 'UPDATE_QUESTION';
  payload: {
    questionType: QuestionType;
    attrs: Question;
  };
};

type ReceiveQuestionAction = {
  type: 'RECEIVE_QUESTION';
  payload: Question;
};

type UpdateQuestionTypeAction = {
  type: 'UPDATE_QUESTION_TYPE';
  payload: {
    questionType: QuestionType;
    newQuestionAttrs: Question;
  };
};

type ResetStateAction = {
  type: 'RESET_STATE';
};

type WritebackReducerActions =
  | ReceiveAllowedAttributesAction
  | UpdateQuestionAction
  | ReceiveQuestionAction
  | UpdateQuestionTypeAction
  | ResetStateAction;

const initialState = {
  question: {},
  module_attributes: null,
  entity_type: null,
  attribute_meta_data: null,
} as const;

export const WritebackContext: React.Context<{
  dispatch: (arg1: WritebackReducerActions) => void;
  state: WritebackContextState;
}> = React.createContext<{
  state: WritebackContextState;
  dispatch: (arg1: WritebackReducerActions) => void;
  // @ts-expect-error - TS2322 - Type '{ readonly question: {}; readonly module_attributes: null; readonly entity_type: null; readonly attribute_meta_data: null; }' is not assignable to type 'WritebackContextState'.
}>({state: initialState, dispatch: () => {}});

export const reducer = (
  state: WritebackContextState,
  action: WritebackReducerActions,
): WritebackContextState => {
  switch (action.type) {
    case RECEIVE_ALLOWED_ATTRIBUTES:
      return {...state, ...action.payload};
    case UPDATE_QUESTION:
      const {questionType, attrs} = action.payload;
      return {
        ...state,
        question: {
          ...state.question,
          [questionType]: {
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'QuestionType' can't be used to index type 'CurrentQuestion'.
            ...state.question[questionType],
            ...attrs,
          },
        },
      };
    case RECEIVE_QUESTION:
      const question = action.payload;
      const questionWithTempTypes = {
        id: question.id,
        selectedType: question.type,
        originalType: question.type, //use to check against changed question type, if applicable
        [question.type]: {
          ...question,
          // @ts-expect-error - TS2339 - Property 'writeback' does not exist on type 'Question'. | TS2339 - Property 'writeback' does not exist on type 'Question'.
          writeback: question.writeback ? question.writeback : {enabled: false},
        },
      } as const;

      return {
        ...state,
        // @ts-expect-error - TS2322 - Type '{ readonly [x: string]: string | { readonly writeback: any; readonly questionTypeUpdated?: boolean; readonly type: "rating_scale_survey_question"; readonly id: string; readonly required: boolean; ... 7 more ...; readonly alertRating?: number; } | ... 7 more ... | { ...; }; readonly id: string; readonly selectedType:...' is not assignable to type 'CurrentQuestion'.
        question: questionWithTempTypes,
      };
    case UPDATE_QUESTION_TYPE:
      const previousQuestionType = state.question.selectedType;
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'QuestionType' can't be used to index type 'CurrentQuestion'.
      const previousQuestionAttrs = state.question[previousQuestionType];

      return {
        ...state,
        question: {
          ...state.question,
          [previousQuestionType]: previousQuestionAttrs,
          [action.payload.questionType]: {
            ...action.payload.newQuestionAttrs,
            id: previousQuestionAttrs.id,
            isNew: previousQuestionAttrs.isNew,
            required: previousQuestionAttrs.required,
            question: previousQuestionAttrs.question,
          },
          selectedType: action.payload.questionType,
        },
      };
    case RESET_STATE:
      return {
        ...state,
        // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type 'CurrentQuestion': selectedType, originalType
        question: {},
      };
    default:
      return state;
  }
};

export const WritebackWrapper = ({children}: {children: React.ReactNode}) => {
  // @ts-expect-error - TS2345 - Argument of type '{ readonly question: {}; readonly module_attributes: null; readonly entity_type: null; readonly attribute_meta_data: null; }' is not assignable to parameter of type 'WritebackContextState'.
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const {workflowId} = useParams();
  const workflow = useSelector((state) => selectWorkflow(state, workflowId));
  const allowedWritebackAttributes = useWritebackAllowedAttributesKeyed(
    // @ts-expect-error - TS2345 - Argument of type 'EntityType | null | undefined' is not assignable to parameter of type 'EntityType'.
    workflow?.entityType,
  );

  React.useEffect(() => {
    dispatch({
      type: RECEIVE_ALLOWED_ATTRIBUTES,
      payload: allowedWritebackAttributes,
    });
  }, [allowedWritebackAttributes]);

  const contextValue = React.useMemo(() => ({state, dispatch}), [state]);

  return (
    <WritebackContext.Provider value={contextValue}>
      {children}
    </WritebackContext.Provider>
  );
};
